import React, { FC, useCallback, useState } from 'react';
import {
  createContext,
  useContextSelector,
} from 'use-context-selector/selectContext';

/** Visibility-only context — only TaskDrawer subscribes; route tree stays outside. */
const TaskPaneVisibilityContext = createContext<boolean>(false);

/** Actions context — setVisibility is stable; route tree uses this to open drawer. */
const TaskPaneActionsContext = createContext<((v: boolean) => void) | null>(
  null,
);

const noop = (_v: boolean) => {};

export const useTaskDrawerContext = () => {
  const visibility = useContextSelector(TaskPaneVisibilityContext, (v) => v);
  const setVisibility = useContextSelector(TaskPaneActionsContext, (s) => s);

  return { visibility, setVisibility: setVisibility ?? noop };
};

/** Use only setVisibility — does NOT re-render when visibility changes. */
export const useTaskDrawerSetVisibility = () => {
  const setVisibility = useContextSelector(TaskPaneActionsContext, (s) => s);

  return setVisibility ?? noop;
};

const StableChildren = React.memo(
  function StableChildren({ children }: { children: React.ReactNode }) {
    return <>{children}</>;
  },
  (prev, next) => prev.children === next.children,
);

StableChildren.displayName = 'TaskPaneStableChildren';

export const TaskPaneContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [visibility, setVisibility] = useState(false);
  const setVisibilityStable = useCallback((v: boolean) => {
    setVisibility(v);
  }, []);

  return (
    <TaskPaneVisibilityContext.Provider value={visibility}>
      <TaskPaneActionsContext.Provider value={setVisibilityStable}>
        <StableChildren>{children}</StableChildren>
      </TaskPaneActionsContext.Provider>
    </TaskPaneVisibilityContext.Provider>
  );
};
